// @ts-check
/** @typedef {import('../types/api').ShowcaseItem} ShowcaseItem */

import { Archive, ArrowDown, ArrowUp, Film, LayoutGrid, List, Pencil, Pin, Plus, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import ShowcaseEditModal from '../components/showcase/ShowcaseEditModal';
import { useToast } from '../context/ToastContext';
import showcaseService from '../services/showcaseService';
import { SHOWCASE_KIND, SHOWCASE_STATUS, hiddenReasonLabel } from '../utils/showcase';

const KIND_TABS = [
  ['', 'All'],
  [SHOWCASE_KIND.SPOTTED, 'Spotted'],
  [SHOWCASE_KIND.UGC, 'UGC'],
];

const STATUS_FILTERS = [
  ['', 'All active'],
  [SHOWCASE_STATUS.DRAFT, 'Draft'],
  [SHOWCASE_STATUS.PUBLISHED, 'Published'],
  [SHOWCASE_STATUS.ARCHIVED, 'Archived'],
];

const chipClass = (/** @type {ShowcaseItem} */ item) => {
  if (item.status === SHOWCASE_STATUS.ARCHIVED) return 'bg-muted text-muted-foreground';
  if (!item.hidden_reason) return 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300';
  if (item.hidden_reason === 'not_published') return 'bg-muted text-muted-foreground';
  return 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300';
};

const statusLabel = (/** @type {ShowcaseItem} */ item) =>
  item.status === SHOWCASE_STATUS.ARCHIVED ? 'Archived' : hiddenReasonLabel(item.hidden_reason);

const Thumb = ({ item, className }) => {
  const src = item.media_type === 'video' ? item.poster_url : item.media_url;
  return (
    <div className={`bg-muted overflow-hidden flex items-center justify-center ${className}`}>
      {src ? (
        <img src={src} alt="" className="w-full h-full object-cover" />
      ) : item.media_type === 'video' && item.media_url ? (
        <video src={item.media_url} muted playsInline preload="metadata" className="w-full h-full object-cover" />
      ) : (
        <Film size={24} className="text-muted-foreground/50" />
      )}
    </div>
  );
};

const ShowcasePage = () => {
  const toast = useToast();
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [view, setView] = useState(/** @type {'grid' | 'list'} */ ('grid'));
  /** @type {[ShowcaseItem[], Function]} */
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(/** @type {ShowcaseItem | null | undefined} */ (undefined)); // undefined = closed, null = new

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await showcaseService.list({ kind, status }));
    } catch (err) {
      toast.error('Failed to load showcase items: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [kind, status, toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Order applies across the whole active list, so reordering is only offered when it is unfiltered.
  const canReorder = !kind && !status;

  const handleMove = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const updated = [...items];
    const [moved] = updated.splice(index, 1);
    updated.splice(target, 0, moved);
    setItems(updated);
    try {
      await showcaseService.reorder(updated.map((i) => i.id));
    } catch (err) {
      toast.error('Failed to reorder: ' + err.message);
      load();
    }
  };

  const handleArchive = async (/** @type {ShowcaseItem} */ item) => {
    if (!window.confirm('Archive this showcase item? It will be hidden from the storefront.')) return;
    try {
      await showcaseService.archive(item.id);
      toast.success('Item archived');
      load();
    } catch (err) {
      toast.error('Failed to archive: ' + err.message);
    }
  };

  const actions = (/** @type {ShowcaseItem} */ item, /** @type {number} */ index) => (
    <div className="flex items-center gap-1">
      {canReorder && (
        <>
          <button
            onClick={() => handleMove(index, -1)}
            disabled={index === 0}
            className="p-1.5 rounded hover:bg-muted disabled:opacity-30"
            aria-label="Move earlier"
          >
            <ArrowUp size={14} />
          </button>
          <button
            onClick={() => handleMove(index, 1)}
            disabled={index === items.length - 1}
            className="p-1.5 rounded hover:bg-muted disabled:opacity-30"
            aria-label="Move later"
          >
            <ArrowDown size={14} />
          </button>
        </>
      )}
      <button onClick={() => setEditing(item)} className="p-1.5 rounded hover:bg-muted" aria-label="Edit">
        <Pencil size={14} />
      </button>
      {item.status !== SHOWCASE_STATUS.ARCHIVED && (
        <button onClick={() => handleArchive(item)} className="p-1.5 rounded hover:bg-muted" aria-label="Archive">
          <Archive size={14} />
        </button>
      )}
    </div>
  );

  const meta = (/** @type {ShowcaseItem} */ item) =>
    [item.person_name, item.product?.name].filter(Boolean).join(' · ') || 'No person or product';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-serif">Showcase</h1>
          <p className="text-sm text-muted-foreground mt-1">Spotted and customer content shown on the storefront</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="flex items-center gap-2 px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={() => setEditing(null)}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg"
          >
            <Plus size={16} />
            Add item
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border">
        <div className="flex overflow-x-auto">
          {KIND_TABS.map(([value, label]) => (
            <button
              key={value || 'all'}
              onClick={() => setKind(value)}
              className={`flex-shrink-0 whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                kind === value
                  ? 'border-foreground text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-input'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 pb-2">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-1.5 text-sm border border-input rounded-lg bg-background"
            aria-label="Status filter"
          >
            {STATUS_FILTERS.map(([value, label]) => (
              <option key={value || 'all'} value={value}>
                {label}
              </option>
            ))}
          </select>
          <div className="flex border border-input rounded-lg overflow-hidden">
            <button
              onClick={() => setView('grid')}
              aria-pressed={view === 'grid'}
              className={`p-2 ${view === 'grid' ? 'bg-primary text-primary-foreground' : ''}`}
              aria-label="Grid view"
            >
              <LayoutGrid size={14} />
            </button>
            <button
              onClick={() => setView('list')}
              aria-pressed={view === 'list'}
              className={`p-2 ${view === 'list' ? 'bg-primary text-primary-foreground' : ''}`}
              aria-label="List view"
            >
              <List size={14} />
            </button>
          </div>
        </div>
      </div>

      {!canReorder && (
        <p className="text-xs text-muted-foreground">Clear the kind and status filters to reorder items.</p>
      )}

      {!loading && items.length === 0 && (
        <div className="bg-card border border-border rounded-xl p-12 text-center text-sm text-muted-foreground/70">
          No showcase items here yet.
        </div>
      )}

      {items.length > 0 && view === 'grid' && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((item, index) => (
            <div key={item.id} className="bg-card border border-border rounded-xl overflow-hidden">
              <Thumb item={item} className="w-full aspect-[4/5]" />
              <div className="p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className={`px-1.5 py-0.5 text-[10px] rounded-md font-medium ${chipClass(item)}`}>
                    {statusLabel(item)}
                  </span>
                  <span className="px-1.5 py-0.5 text-[10px] rounded-md font-medium bg-muted text-muted-foreground uppercase">
                    {item.kind}
                  </span>
                  {item.is_pinned && <Pin size={11} className="text-muted-foreground" />}
                </div>
                <p className="text-xs text-muted-foreground truncate">{meta(item)}</p>
                {actions(item, index)}
              </div>
            </div>
          ))}
        </div>
      )}

      {items.length > 0 && view === 'list' && (
        <div className="bg-card border border-border rounded-xl divide-y divide-border">
          {items.map((item, index) => (
            <div key={item.id} className="flex items-center gap-3 p-3">
              <Thumb item={item} className="w-12 h-14 rounded-md flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{item.caption || meta(item)}</p>
                <p className="text-xs text-muted-foreground truncate">{meta(item)}</p>
              </div>
              <span className="hidden sm:inline px-1.5 py-0.5 text-[10px] rounded-md font-medium bg-muted text-muted-foreground uppercase">
                {item.kind}
              </span>
              <span className={`px-1.5 py-0.5 text-[10px] rounded-md font-medium whitespace-nowrap ${chipClass(item)}`}>
                {statusLabel(item)}
              </span>
              {actions(item, index)}
            </div>
          ))}
        </div>
      )}

      {editing !== undefined && (
        <ShowcaseEditModal
          item={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            load();
          }}
        />
      )}
    </div>
  );
};

export default ShowcasePage;
